import {
  legUploadVast,
  uploadStroomViaSession,
  uploadViaSession,
  vraagSessieStand,
  vraagUploadUrl,
  UploadProgressCallback,
} from "../documents";
import { maakCdZipStroom } from "../components/dossier/cdZip";
import {
  BestandWijziging,
  werkBestandBij,
} from "../services/uploadBatchService";
import { grootteVan, QueuedUploadItem } from "./uploadQueueReducer";
import { Gebruikersfout } from "../utils/foutmelding";

export interface UploadContext {
  dossierKenmerk: string;
  batchId: string;
  /** Aanmaakmoment van de batch: de vaste datum in de cd-zip. */
  aangemaaktOp: string;
  /** Wacht bij een 401 tot er weer is ingelogd (UploadQueueContext). */
  metSessie: <T>(verzoek: () => PromiseLike<T>) => Promise<T>;
  onProgress: UploadProgressCallback;
}

/** Zo vaak hooguit een voortgangsstand naar het register; Graph weet het precies. */
const VOORTGANG_INTERVAL_MS = 15_000;

/**
 * Commit op het pad lukte niet zonder dat er iets mis is: het bestand staat er
 * niet (sessie verlopen), of het bestand op dat pad is al een document en dus
 * niet zeker deze upload (A2-9). Dan verder via upload-url.
 */
const isBestandWeg = (err: unknown) => {
  const r = (
    err as { response?: { status?: number; data?: { message?: unknown } } }
  )?.response;
  return (
    (r?.status === 400 && r?.data?.message === "bestand_niet_gevonden") ||
    (r?.status === 409 && r?.data?.message === "bestand_al_vastgelegd")
  );
};
/**
 * 409 bestand_al_vastgelegd met bestaatAl: precies dit bestand staat al als
 * document in het dossier. Net als bestaatAl bij upload-url: overslaan, en geen
 * nieuwe upload-url, want die ziet het bestand niet in de maplijst van de
 * batch en maakt een tweede "(1)" (B3, review 1).
 */
const alVastgelegd = (err: unknown): { documentId?: string } | null => {
  const r = (
    err as {
      response?: {
        status?: number;
        data?: { message?: unknown; bestaatAl?: unknown; documentId?: unknown };
      };
    }
  )?.response;
  if (
    r?.status !== 409 ||
    r.data?.message !== "bestand_al_vastgelegd" ||
    r.data.bestaatAl !== true
  ) {
    return null;
  }
  return {
    documentId:
      typeof r.data.documentId === "string" ? r.data.documentId : undefined,
  };
};
const korteFout = (err: unknown) =>
  String((err as Error)?.message ?? err).slice(0, 300);
/** Wat de gebruiker doet als het vastleggen mislukt (trein 51, ronde 2). */
const VASTLEGGEN_MISLUKT =
  "Het vastleggen is mislukt. Zet het bestand nog een keer in het dossier.";
/** De fout van een mislukte commit als code: status plus de foutcode van de server (trein 51, L4). */
const vastleggenCode = (err: unknown) => {
  const r = (
    err as { response?: { status?: number; data?: { message?: unknown } } }
  )?.response;
  const code =
    typeof r?.data?.message === "string" &&
    /^[a-z0-9_]{1,60}$/.test(r.data.message)
      ? `_${r.data.message}`
      : "";
  return `vastleggen_mislukt_${r?.status ?? "netwerk"}${code}`;
};

/**
 * Mislukt terwijl het bestand een uploadsessie had: het register houdt het
 * BEZIG, dus een tabblad dat de batch overneemt, maakt het af. Zonder sessie
 * staat het op MISLUKT en neemt niemand het over (B3, review 3). `oorzaak` is
 * de echte fout, voor de melding.
 */
export class FoutMetSessie extends Error {
  oorzaak: unknown;
  constructor(oorzaak: unknown) {
    super(korteFout(oorzaak));
    this.name = "FoutMetSessie";
    this.oorzaak = oorzaak;
  }
}

/**
 * Eén bestand of cd-zip van begin tot eind (I18, WP3): hervat een lopende
 * Graph-sessie, slaat een bestand over dat er al staat, of uploadt vers, en
 * houdt het register bij. Graph-PUT's hebben de sessie van de webapp niet
 * nodig; upload-url, commit en statuswijzigingen wachten bij een 401.
 */
export async function verwerkUploadItem(
  ctx: UploadContext,
  item: QueuedUploadItem,
  bestandId: string,
): Promise<"geupload" | "overgeslagen"> {
  const grootte = grootteVan(item);
  const mime =
    "cd" in item
      ? "application/zip"
      : item.file.type || "application/octet-stream";
  const meld = (w: BestandWijziging) =>
    ctx.metSessie(() =>
      werkBestandBij(ctx.dossierKenmerk, ctx.batchId, bestandId, w),
    );
  let laatste = 0;
  const voortgang: UploadProgressCallback = (p) => {
    ctx.onProgress(p);
    if (Date.now() - laatste < VOORTGANG_INTERVAL_MS) return;
    laatste = Date.now();
    void werkBestandBij(ctx.dossierKenmerk, ctx.batchId, bestandId, {
      geuploadBytes: p.uploadedBytes,
    }).catch(() => undefined);
  };
  /** Geeft het item-id uit het laatste sessieantwoord, of null (A2-9). */
  const stuur = async (
    uploadUrl: string,
    headers: Record<string, string>,
    vanaf: number,
  ): Promise<string | null> =>
    "cd" in item
      ? uploadStroomViaSession(
          uploadUrl,
          headers,
          await maakCdZipStroom(item.cd, new Date(ctx.aangemaaktOp)),
          grootte,
          vanaf,
          voortgang,
        )
      : uploadViaSession(uploadUrl, headers, item.file, voortgang, vanaf);
  // De commit mislukte (geen 409 met bestaatAl): de bytes staan er, dus niet
  // BEZIG laten, anders komt "Upload onderbroken" steeds terug (trein 51, L4).
  let vastleggenMislukt = false;
  const legVast = async (blobName: string, itemId?: string | null) => {
    let id: string;
    try {
      ({ id } = await ctx.metSessie(() =>
        legUploadVast(ctx.dossierKenmerk, {
          blobName,
          originalFilename: item.naam,
          mimeType: mime,
          size: grootte,
          category: item.category,
          ...(itemId ? { itemId } : {}),
        }),
      ));
    } catch (err) {
      const al = alVastgelegd(err);
      if (!al) {
        vastleggenMislukt = true;
        throw err;
      }
      await meld({ status: "OVERGESLAGEN", documentId: al.documentId });
      return "overgeslagen" as const;
    }
    await meld({ status: "KLAAR", documentId: id });
    return "geupload" as const;
  };

  let sessie: string | null = item.hervat?.uploadUrl ?? null;
  try {
    const vorige = item.hervat;
    if (vorige?.uploadUrl && vorige.blobName) {
      const vanaf = await vraagSessieStand(vorige.uploadUrl);
      if (vanaf !== null) {
        const itemId = await stuur(
          vorige.uploadUrl,
          { "Content-Type": mime },
          vanaf,
        );
        return await legVast(vorige.blobName, itemId);
      }
      // Geen sessie meer: de upload was af (dan staat het bestand er) of is verlopen.
      try {
        return await legVast(vorige.blobName);
      } catch (err) {
        if (!isBestandWeg(err)) throw err;
        vastleggenMislukt = false; // weg: gewoon opnieuw uploaden
      }
      sessie = null;
    }
    const antwoord = await ctx.metSessie(() =>
      vraagUploadUrl(ctx.dossierKenmerk, {
        filename: item.naam,
        mimeType: mime,
        size: grootte,
        category: item.category,
        folderPath: item.targetFolder,
        batchId: ctx.batchId,
      }),
    );
    if (antwoord.bestaatAl) {
      await meld({ status: "OVERGESLAGEN", documentId: antwoord.documentId });
      return "overgeslagen";
    }
    await meld({
      status: "BEZIG",
      uploadUrl: antwoord.uploadUrl,
      blobName: antwoord.blobName,
    });
    sessie = antwoord.uploadUrl;
    const itemId = await stuur(antwoord.uploadUrl, antwoord.headers, 0);
    return await legVast(antwoord.blobName, itemId);
  } catch (err) {
    if (vastleggenMislukt) {
      // MISLUKT met de fout als code; vóór de volgende registerlezing, zodat
      // de gebruiker de fout één keer ziet en geen "Upload onderbroken".
      await werkBestandBij(ctx.dossierKenmerk, ctx.batchId, bestandId, {
        status: "MISLUKT",
        fout: vastleggenCode(err),
      }).catch(() => undefined);
      // De kale 409 houdt zijn eigen tekst: opnieuw zetten maakt daar een
      // kopie (B3, low 6). Anders zegt de melding wat te doen (ronde 2).
      const r = (
        err as { response?: { status?: number; data?: { message?: unknown } } }
      )?.response;
      if (r?.status === 409 && r.data?.message === "bestand_al_vastgelegd") {
        throw err;
      }
      throw new Gebruikersfout(VASTLEGGEN_MISLUKT, { cause: err });
    }
    // Met een sessie blijft het bestand BEZIG: na herladen gaat het verder waar Graph is.
    void werkBestandBij(
      ctx.dossierKenmerk,
      ctx.batchId,
      bestandId,
      sessie
        ? { fout: korteFout(err) }
        : { status: "MISLUKT", fout: korteFout(err) },
    ).catch(() => undefined);
    throw sessie ? new FoutMetSessie(err) : err;
  }
}
